import * as React from 'react';
import queryString from 'query-string';
import { useHistory } from 'react-router-dom';
import {
  UserRoles,
  TransferState,
  TransferType,
  UserStatusTypes,
  userRolesToLabelMap,
} from '@bb/users';
import { AuthContext, AuthContextType } from 'contexts';
import { BreadcrumbProps } from 'components/Breadcrumb';
import type {
  IUserOrgResponse as ActiveUsers,
  IUserGetTransfersResponse as TransfersDTO,
} from '@/app-package';
import { featureEnabled } from 'utils';
import {
  useGetUsers,
  useGetDeletedUsers,
  DeletedUser,
  Group,
  GroupInfo,
  useGetGroups,
  useGetGroupMembers,
  Team,
  TeamInfo,
  useGetTeamMembers,
  useGetTeams,
  useGetTransfers,
} from './Hooks';

import { GroupMember } from './util';

export interface SortControls {
  sort: string;
  isDesc: boolean;
}

const ManageUsersContext = React.createContext<ManageUsersContextType>(null);

function getInitialCheckedOptions(locations: string[]) {
  return locations.reduce((acc: Record<string, boolean>, curr: string) => {
    acc[curr] = true;
    return acc;
  }, {});
}

function getDefaultBranchOptions(
  auth: AuthContextType['auth'],
): BranchOptionType[] {
  let branchOptions = auth?.org?.locations.reduce((acc, cur, index) => {
    if (!cur.isDeleted) {
      acc.push({ label: cur.name, value: index.toString() });
    }
    return acc;
  }, []);

  if (auth?.role === UserRoles['Branch Admin']) {
    const branchAdminMultiLocations = new Set([
      auth?.location,
      ...(auth?.locations || []),
    ]);
    branchOptions = branchOptions.filter(({ value }) =>
      branchAdminMultiLocations.has(value),
    );
  }
  return branchOptions;
}

export type BranchOptionType = { label: string; value: string };
export type TabKeyType = 'users' | 'transfers' | 'groups' | 'teams' | 'deleted';
const tabKeyTypes = ['users', 'transfers', 'groups', 'teams', 'deleted'];

type ManageUsersContextType = {
  breadcrumbRoutes: BreadcrumbProps['routes'];
  activeUsers: ActiveUsers['users'];
  activeUsersCount: number;
  groups: Group[];
  groupsCount: number;
  groupId: string;
  fetchGroups: () => Promise<void>;
  fetchGroupMembers: (active: boolean) => Promise<void>;
  selectedGroupInfo: GroupInfo;
  selectedGroupMemberCount: number;
  selectedGroupUnfilteredUsers: GroupMember[];
  teams: Team[];
  teamsCount: number;
  teamId: string;
  fetchTeams: () => Promise<void>;
  fetchTeamMembers: (active: boolean) => Promise<void>;
  selectedTeamInfo: TeamInfo;
  selectedTeamMemberCount: number;
  deletedUsers: DeletedUser[];
  deletedUsersCount: number;
  existingDeletedUserRoles: (keyof typeof UserRoles)[];
  transfers: TransfersDTO['transfers'];
  transferCount: number;
  ongoingTransfersCount: number;
  pageNumber: number;
  branchFilterText: string;
  branchOptions: BranchOptionType[];
  filteredBranchOptions: Record<string, boolean>;
  statusFilters: UserStatusTypes[];
  roleFilters: (keyof typeof UserRoles)[];
  existingUserStatus: UserStatusTypes[];
  existingUserRoles: (keyof typeof UserRoles)[];
  transferStateFilters: TransferState[];
  transferTypeFilters: TransferType[];
  isUsersLoading: boolean;
  isGroupsLoading: boolean;
  isGroupMemberLoading: boolean;
  isTeamsLoading: boolean;
  isTeamMemberLoading: boolean;
  isDeletedLoading: boolean;
  isTransfersLoading: boolean;
  locations: string[];
  searchText: string;
  selectedTabKey: TabKeyType;
  usersSortControls: SortControls;
  transfersSortControls: SortControls;
  selectedUsers: string[];
  setBreadcrumbRoutes: React.Dispatch<
    React.SetStateAction<BreadcrumbProps['routes']>
  >;
  setActiveUsers: React.Dispatch<React.SetStateAction<ActiveUsers['users']>>;
  setGroupId: React.Dispatch<React.SetStateAction<string>>;
  setTeamId: React.Dispatch<React.SetStateAction<string>>;
  setBranchFilterText: React.Dispatch<string>;
  setBranchOptions: React.Dispatch<BranchOptionType[]>;
  setFilteredBranchOptions: React.Dispatch<Record<string, boolean>>;
  setTransfers: React.Dispatch<React.SetStateAction<TransfersDTO['transfers']>>;
  setStatusFilters: React.Dispatch<UserStatusTypes[]>;
  setRoleFilters: React.Dispatch<(keyof typeof UserRoles)[]>;
  setTransferStateFilters: React.Dispatch<TransferState[]>;
  setTransferTypeFilters: React.Dispatch<TransferType[]>;
  setLocations: React.Dispatch<React.SetStateAction<string[]>>;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setSelectedTabKey: React.Dispatch<React.SetStateAction<TabKeyType>>;
  setUsersSortControls: React.Dispatch<React.SetStateAction<SortControls>>;
  setTransfersSortControls: React.Dispatch<React.SetStateAction<SortControls>>;
  setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>;
  resetFilteredBranchOptions: () => void;
};

export const ManageUsersContextProvider: React.FC = ({ children }) => {
  const { auth, hasFeature } = React.useContext(AuthContext);
  const history = useHistory();
  const query = queryString.parse(history.location.search, {
    arrayFormat: 'bracket',
  });

  const queryTab = query.tabKey?.toString();
  const [breadcrumbRoutes, setBreadcrumbRoutes] = React.useState<
    BreadcrumbProps['routes']
  >([]);
  const [selectedTabKey, setSelectedTabKey] = React.useState<TabKeyType>(
    tabKeyTypes.includes(queryTab) ? (queryTab as TabKeyType) : 'users',
  );
  const [searchText, setSearchText] = React.useState<string>(
    query.userName?.toString() || '',
  );
  const [pageNumber, setPageNumber] = React.useState(
    Number(query.pageNumber) || 1,
  );

  const [locations, setLocations] = React.useState(
    Array.isArray(query.locations) ? query.locations.filter(Boolean) : [],
  );
  const [branchOptions, setBranchOptions] = React.useState(
    getDefaultBranchOptions(auth),
  );
  const [filteredBranchOptions, setFilteredBranchOptions] = React.useState(
    getInitialCheckedOptions(locations),
  );
  const [branchFilterText, setBranchFilterText] = React.useState('');
  const [usersSortControls, setUsersSortControls] =
    React.useState<SortControls>({
      sort: (query.usersSort as string) || 'firstName',
      isDesc: query?.usersOrder === 'desc',
    });
  const [transfersSortControls, setTransfersSortControls] = React.useState({
    sort: (query.transfersSort as string) || 'status',
    isDesc: query.transfersOrder ? query.transferOrder === 'desc' : true,
  });
  const [groupId, setGroupId] = React.useState('');
  const [teamId, setTeamId] = React.useState('');

  const [selectedUsers, setSelectedUsers] = React.useState([]);

  const [statusFilters, setStatusFilters] = React.useState<UserStatusTypes[]>(
    Array.isArray(query?.statusFilters)
      ? (query.statusFilters as UserStatusTypes[])?.filter(Boolean)
      : [],
  );

  const [roleFilters, setRoleFilters] = React.useState<
    (keyof typeof UserRoles)[]
  >(
    (Array.isArray(query?.roleFilters)
      ? (query.roleFilters as UserRoles[])
          ?.filter(Boolean)
          .map(key => userRolesToLabelMap[key])
      : []) as (keyof typeof UserRoles)[],
  );

  const [transferStateFilters, setTransferStateFilters] = React.useState<
    TransferState[]
  >(
    Array.isArray(query?.transferStateFilters)
      ? (query.transferStateFilters as TransferState[])?.filter(Boolean)
      : [TransferState.Pending],
  );
  const [transferTypeFilters, setTransferTypeFilters] = React.useState<
    TransferType[]
  >(
    Array.isArray(query?.transferTypeFilters)
      ? (query.transferTypeFilters as TransferType[])?.filter(Boolean)
      : [],
  );

  React.useEffect(() => {
    if (queryTab === 'teams' && !hasFeature('TEAMS'))
      history.push('/feature-missing');

    setSelectedTabKey(
      tabKeyTypes.includes(queryTab) ? (queryTab as TabKeyType) : 'users',
    );
  }, [queryTab]);

  // Clear transfer state filters when using search filter
  React.useEffect(() => {
    if (searchText) setTransferStateFilters(curr => (curr.length ? [] : curr));
  }, [searchText]);

  React.useEffect(
    () => () => {
      setPageNumber(1);
    },
    [
      selectedTabKey,
      searchText,
      locations,
      statusFilters,
      roleFilters,
      transferStateFilters,
      transferTypeFilters,
      usersSortControls,
      transfersSortControls,
    ],
  );

  React.useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);
    searchParams.set('tabKey', selectedTabKey);

    // Deleted api doesn't take in a pageNumber so writing this here instead of in the hook
    if (selectedTabKey === 'deleted')
      searchParams.set('pageNumber', pageNumber.toString());

    history.replace({ search: searchParams.toString() });
  }, [selectedTabKey, pageNumber, history]);

  const {
    activeUsersCount,
    activeUsers,
    existingUserRoles,
    existingUserStatus,
    setActiveUsers,
    isLoading: isUsersLoading,
  } = useGetUsers({
    shouldFetch: selectedTabKey === 'users',
    pageNumber,
    locations,
    searchText,
    statusFilters,
    roleFilters,
    existingTypeLocations: [],
    excludeGeneratedAdmin: true,
    ...usersSortControls,
    ...(featureEnabled('membershipKeyholderIdSupportFeature', 1)
      ? { searchByNameOrId: true }
      : {}),
  });

  const {
    groups,
    fetchGroups,
    groupsCount,
    isLoading: isGroupsLoading,
  } = useGetGroups({
    shouldFetch: selectedTabKey === 'groups' && groupId === '',
    searchText,
    orgId: auth.org.id,
  });
  const {
    groupInfo: selectedGroupInfo,
    groupMembersCount: selectedGroupMemberCount,
    isLoading: isGroupMemberLoading,
    fetchGroupMembers,
    unfilteredUsers: selectedGroupUnfilteredUsers,
  } = useGetGroupMembers({ groupId, searchText });

  const {
    teams,
    fetchTeams,
    teamsCount,
    isLoading: isTeamsLoading,
  } = useGetTeams({
    shouldFetch: selectedTabKey === 'teams' && teamId === '',
    searchText,
    orgId: auth.org.id,
  });
  const {
    teamInfo: selectedTeamInfo,
    teamMembersCount: selectedTeamMemberCount,
    isLoading: isTeamMemberLoading,
    fetchTeamMembers,
  } = useGetTeamMembers({ teamId, searchText });

  const {
    deletedUsers,
    isLoading: isDeletedLoading,
    deletedUsersCount,
    existingDeletedUserRoles,
  } = useGetDeletedUsers({
    shouldFetch: selectedTabKey === 'deleted',
    locations,
    searchText,
    roleFilters,
  });

  const {
    transfers,
    isLoading: isTransfersLoading,
    transferCount,
    ongoingCount: ongoingTransfersCount,
    setTransfers,
  } = useGetTransfers({
    shouldFetch: selectedTabKey === 'transfers',
    pageNumber,
    searchText,
    stateFilters: transferStateFilters,
    typeFilters: transferTypeFilters,
    ...transfersSortControls,
  });

  const resetFilteredBranchOptions = React.useCallback(
    () => setFilteredBranchOptions(getInitialCheckedOptions(locations)),
    [locations],
  );
  return (
    <ManageUsersContext.Provider
      value={{
        breadcrumbRoutes: [
          { breadcrumbName: 'Admin', path: '/admin' },
          { breadcrumbName: 'Manage Users', path: '/users/list' },
          ...breadcrumbRoutes,
        ],
        activeUsers,
        activeUsersCount,
        groupsCount,
        groups,
        groupId,
        fetchGroups,
        fetchGroupMembers,
        teamsCount,
        teams,
        teamId,
        fetchTeams,
        fetchTeamMembers,
        selectedGroupInfo,
        selectedGroupMemberCount,
        selectedGroupUnfilteredUsers,
        selectedTeamInfo,
        selectedTeamMemberCount,
        deletedUsers,
        deletedUsersCount,
        existingDeletedUserRoles,
        transfers,
        transferCount,
        ongoingTransfersCount,
        branchFilterText,
        branchOptions,
        filteredBranchOptions,
        statusFilters,
        roleFilters,
        existingUserStatus,
        existingUserRoles,
        transferStateFilters,
        transferTypeFilters,
        isUsersLoading,
        isGroupsLoading,
        isGroupMemberLoading,
        isTeamsLoading,
        isTeamMemberLoading,
        isDeletedLoading,
        isTransfersLoading,
        locations,
        pageNumber,
        searchText,
        selectedTabKey,
        usersSortControls,
        transfersSortControls,
        selectedUsers,
        setBreadcrumbRoutes,
        setActiveUsers,
        setBranchFilterText,
        setBranchOptions,
        setFilteredBranchOptions,
        setTransfers,
        setStatusFilters,
        setRoleFilters,
        setTransferStateFilters,
        setTransferTypeFilters,
        setGroupId,
        setTeamId,
        setLocations,
        setPageNumber,
        setSearchText,
        setSelectedTabKey,
        setUsersSortControls,
        setTransfersSortControls,
        setSelectedUsers,
        resetFilteredBranchOptions,
      }}
    >
      {children}
    </ManageUsersContext.Provider>
  );
};
export default ManageUsersContext;
